import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faSync, faTrash, faStar, faComments, faCalendarCheck, 
    faUserEdit, faQuoteLeft, faTimes, faPhone, faIdBadge 
} from '@fortawesome/free-solid-svg-icons';
import { API_BASE } from '../apiConfig';

const AdminReviews = () => {
    const { t, i18n } = useTranslation();
    const [reviews, setReviews] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [userLoading, setUserLoading] = useState(false);

    // Լեզվին համապատասխան ամսաթվի ձևաչափ
    const locale = i18n.language === 'hy' ? 'hy-AM' : i18n.language === 'ru' ? 'ru-RU' : 'en-US';

    const getHeaders = useCallback(() => ({
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        'Content-Type': 'application/json',
        'ngrok-skip-browser-warning': 'true'
    }), []);

    const loadReviews = useCallback(async () => {
        setLoading(true);
        try {
            const res = await fetch(`${API_BASE}/api/reviews`, { headers: getHeaders() });
            if (res.status === 401) return;
            const data = await res.json();
            setReviews(Array.isArray(data) ? data : []);
        } catch (e) { 
            console.error(e); 
        } finally { 
            setLoading(false); 
        }
    }, [getHeaders]);

    useEffect(() => { loadReviews(); }, [loadReviews]);

    const fetchFullUserData = async (review) => {
        setUserLoading(true);
        try {
            const res = await fetch(`${API_BASE}/api/admin-users`, { headers: getHeaders() });
            const allUsers = await res.json();
            const foundUser = allUsers.find(u => u.full_name === review.name || u.id === review.user_id);
            
            setSelectedUser(foundUser || { 
                full_name: review.name, 
                is_guest: true,
                created_at: review.created_at 
            });
        } catch (e) {
            setSelectedUser({ full_name: review.name });
        } finally {
            setUserLoading(false);
        }
    };

    const stats = useMemo(() => ({
        total: reviews.length,
        avg: reviews.length > 0 ? (reviews.reduce((acc, r) => acc + (Number(r.stars) || 0), 0) / reviews.length).toFixed(1) : 0,
        today: reviews.filter(r => new Date(r.created_at).toDateString() === new Date().toDateString()).length
    }), [reviews]);

    const deleteReview = async (id) => {
        if (!window.confirm(t('confirm_delete_review'))) return;
        try {
            const res = await fetch(`${API_BASE}/api/reviews/${id}`, { method: 'DELETE', headers: getHeaders() });
            if (res.ok) setReviews(prev => prev.filter(r => r.id !== id));
        } catch (e) { console.error(e); }
    };

    return (
        <div style={styles.dashboard}>
            <div style={styles.analyticsRow}>
                <AnalyticCard title={t('reviews_analytics_title')} value={stats.total} icon={faComments} grad="linear-gradient(135deg, #4318FF 0%, #5E37FF 100%)" />
                <AnalyticCard title={t('rating_analytics_title')} value={`${stats.avg} / 5`} icon={faStar} grad="linear-gradient(135deg, #FF9933 0%, #FFB366 100%)" />
                <AnalyticCard title={t('today_analytics_title')} value={stats.today} icon={faCalendarCheck} grad="linear-gradient(135deg, #01B574 0%, #2AF598 100%)" />
            </div>

            <div style={styles.mainHeader}>
                <h2 style={styles.title}>{t('admin_reviews_main_title')}</h2>
                <button onClick={loadReviews} style={styles.refreshCircle}>
                    <FontAwesomeIcon icon={faSync} spin={loading} />
                </button>
            </div>

            <div style={styles.reviewsGrid}>
                {reviews.map(r => (
                    <div key={r.id} style={styles.reviewCard}>
                        <div style={styles.cardTop}>
                            <div style={styles.starsRow}>
                                {[...Array(5)].map((_, i) => (
                                    <FontAwesomeIcon key={i} icon={faStar} style={{ color: i < (Number(r.stars) || 0) ? '#FF9933' : '#E0E5F2' }} />
                                ))}
                            </div>
                            <span style={styles.dateText}>{new Date(r.created_at).toLocaleDateString(locale)}</span>
                        </div>

                        <div style={styles.quoteBox}>
                            <FontAwesomeIcon icon={faQuoteLeft} style={styles.quoteIcon} />
                            <p style={styles.reviewText}>{r.text}</p>
                        </div>

                        <div style={styles.cardFooter}>
                            <div style={styles.userInfo} onClick={() => fetchFullUserData(r)} title={t('view_user_data_tooltip')}>
                                <div style={styles.userIcon}><FontAwesomeIcon icon={faUserEdit} /></div>
                                <span style={styles.userName}>{r.name || t('anonymous_user')}</span>
                            </div>
                            <button onClick={() => deleteReview(r.id)} style={styles.deleteBtn}>
                                <FontAwesomeIcon icon={faTrash} />
                            </button>
                        </div>
                    </div>
                ))}
                {reviews.length === 0 && !loading && <div style={{textAlign: 'center', gridColumn: '1/-1', color: '#A3AED0'}}>{t('no_reviews_yet')}</div>}
            </div>

            {selectedUser && (
                <div style={styles.modalOverlay} onClick={() => setSelectedUser(null)}>
                    <div style={styles.modalContent} onClick={e => e.stopPropagation()}>
                        <div style={styles.modalHeaderGrad}></div>
                        <button style={styles.closeModal} onClick={() => setSelectedUser(null)}><FontAwesomeIcon icon={faTimes} /></button>
                        <div style={styles.bigAvatarWrapper}>
                            {selectedUser.profile_pic ? (
              <img 
    src={selectedUser.profile_pic ? `${API_BASE}${selectedUser.profile_pic}` : '/assets/img/default-avatar.png'} 
    style={styles.bigAvatarImg} 
    alt={selectedUser.name || "User Profile"} 
    loading="eager"      // 1. Սա կարևոր նկար է, բեռնիր անմիջապես
    fetchPriority="high" // 2. Տուր ամենաբարձր առաջնահերթությունը
    decoding="sync"      // 3. Քանի որ մեծ նկար է, թող միանգամից ռենդեր լինի
    onError={(e) => { 
        e.target.onerror = null; 
        e.target.src = 'https://cdn-icons-png.flaticon.com/512/149/149071.png'; 
    }} 
/>
                            ) : (
                                <div style={styles.profileInitial}>{selectedUser.full_name?.[0] || '?'}</div>
                            )}
                        </div>
                        <h2 style={styles.modalName}>{selectedUser.full_name || t('anonymous_user')}</h2>
                        <span style={styles.userBadge}>{selectedUser.is_guest ? t('guest_badge') : t('verified_reviewer_badge')}</span>
                        <div style={styles.modalBody}>
                            <InfoRow icon={faPhone} label={t('label_phone')} val={selectedUser.phone} notFound={t('not_specified')} />
                            <InfoRow icon={faIdBadge} label={t('label_user_id')} val={selectedUser.id ? `#${selectedUser.id}` : t('n_a')} />
                            <InfoRow icon={faCalendarCheck} label={t('label_registration_date')} val={selectedUser.created_at ? new Date(selectedUser.created_at).toLocaleDateString(locale) : t('n_a')} />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

const AnalyticCard = ({ title, value, icon, grad }) => (
    <div style={{...styles.anaCard, background: grad}}>
        <div style={styles.anaIconBox}><FontAwesomeIcon icon={icon} /></div>
        <div><p style={styles.anaLabel}>{title}</p><h3 style={styles.anaVal}>{value}</h3></div>
    </div>
);

const InfoRow = ({ icon, label, val, notFound }) => (
    <div style={styles.infoCard}>
        <div style={styles.infoIconBox}><FontAwesomeIcon icon={icon} /></div>
        <div><p style={styles.infoLabel}>{label}</p><p style={styles.infoVal}>{val || notFound}</p></div>
    </div>
);

const styles = {
    dashboard: { padding: '30px', backgroundColor: '#F4F7FE', minHeight: '100vh', fontFamily: "'Plus Jakarta Sans', sans-serif" },
    analyticsRow: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '20px', marginBottom: '40px' },
    anaCard: { padding: '25px', borderRadius: '24px', color: '#fff', display: 'flex', alignItems: 'center', gap: '20px' },
    anaIconBox: { width: '45px', height: '45px', borderRadius: '12px', background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' },
    anaLabel: { fontSize: '11px', margin: 0, opacity: 0.8, textTransform: 'uppercase' },
    anaVal: { fontSize: '24px', margin: '5px 0 0 0', fontWeight: '800' },
    mainHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' },
    title: { fontSize: '24px', fontWeight: '800', color: '#1B2559' },
    refreshCircle: { width: '45px', height: '45px', borderRadius: '50%', border: 'none', background: '#fff', color: '#4318FF', cursor: 'pointer', boxShadow: '0 4px 10px rgba(0,0,0,0.05)', display: 'flex', alignItems: 'center', justifyContent: 'center' },
    reviewsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: '25px' },
    reviewCard: { background: '#fff', padding: '25px', borderRadius: '24px', boxShadow: '0 10px 25px rgba(0,0,0,0.02)', display: 'flex', flexDirection: 'column' },
    cardTop: { display: 'flex', justifyContent: 'space-between', marginBottom: '15px' },
    starsRow: { display: 'flex', gap: '3px', fontSize: '13px' },
    dateText: { fontSize: '12px', color: '#A3AED0', fontWeight: '600' },
    quoteBox: { position: 'relative', marginBottom: '20px' },
    quoteIcon: { position: 'absolute', top: '-10px', left: '-10px', fontSize: '18px', color: '#F4F7FE' },
    reviewText: { fontSize: '14px', color: '#475569', lineHeight: '1.6', margin: 0, fontStyle: 'italic', position: 'relative', zIndex: 1 },
    cardFooter: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: '15px', borderTop: '1px solid #F4F7FE' },
    userInfo: { display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' },
    userIcon: { width: '30px', height: '30px', borderRadius: '50%', background: '#F4F7FE', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#4318FF' },
    userName: { fontSize: '14px', fontWeight: '700', color: '#1B2559' },
    deleteBtn: { background: '#FEE2E2', color: '#EF4444', border: 'none', width: '35px', height: '35px', borderRadius: '10px', cursor: 'pointer' },
    modalOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(15, 23, 42, 0.6)', backdropFilter: 'blur(8px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
    modalContent: { background: '#fff', width: '350px', borderRadius: '32px', overflow: 'hidden', position: 'relative', textAlign: 'center' },
    modalHeaderGrad: { height: '80px', background: 'linear-gradient(135deg, #4318FF 0%, #B233FF 100%)' },
    closeModal: { position: 'absolute', top: '20px', right: '20px', border: 'none', background: 'rgba(255,255,255,0.2)', width: '30px', height: '30px', borderRadius: '50%', cursor: 'pointer', color: '#fff' },
    bigAvatarWrapper: { width: '100px', height: '100px', borderRadius: '30px', border: '4px solid #fff', margin: '-50px auto 10px', overflow: 'hidden', background: '#fff', boxShadow: '0 10px 20px rgba(0,0,0,0.1)' },
    bigAvatarImg: { width: '100%', height: '100%', objectFit: 'cover' },
    profileInitial: { width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '35px', fontWeight: 'bold', background: '#4318FF', color: 'white' },
    modalName: { fontSize: '22px', fontWeight: '800', color: '#1B2559', margin: '0' },
    userBadge: { fontSize: '10px', background: '#E6FAF5', color: '#01B574', padding: '4px 12px', borderRadius: '20px', fontWeight: 'bold', display: 'inline-block', marginTop: '8px' },
    modalBody: { padding: '20px', textAlign: 'left', display: 'grid', gap: '10px' },
    infoCard: { background: '#F4F7FE', padding: '12px', borderRadius: '15px', display: 'flex', alignItems: 'center', gap: '12px' },
    infoIconBox: { width: '35px', height: '35px', background: '#fff', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#4318FF' },
    infoLabel: { fontSize: '10px', color: '#A3AED0', margin: 0, fontWeight: 'bold' },
    infoVal: { fontSize: '14px', color: '#1B2559', margin: 0, fontWeight: '700' }
};

export default AdminReviews;